import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Plus,
  AlertCircle,
  Calendar,
  Loader2,
  Edit3,
  Save,
  X as XIcon,
  Trash2,
  TrendingUp,
  TrendingDown,
  Building2,
  Undo2,
  Goal,
  ChevronDown,
  Info,
  Sparkles
} from 'lucide-react'
// Using native title attr for KPI descriptions — Radix tooltips stick on fast hover
import { StrategicContextSelector, type StrategicContext } from '@/components/strategic/StrategicContextSelector'
import type { PendingObjective, KPI, RejectedObjective } from './types'
import { formatDate } from './types'
import { objectiveYear } from '@/lib/objectiveYear'

interface ObjectiveCardProps {
  objective: PendingObjective
  allowEdit?: boolean
  isEditing: boolean
  currentObj: PendingObjective
  savingEdit: number | null
  editedObjectives: Record<number, PendingObjective>
  canWithdrawObjectives: boolean
  withdrawingObjective: number | null
  userRole: 'manager' | 'employee'
  objectivesLookup: any[]
  initiativesLookup: any[]
  operationalLookup: any[]
  onStartEdit: (objective: PendingObjective) => void
  onCancelEdit: (objectiveId: number) => void
  onSaveEdit: (objectiveId: number) => void
  onUpdateField: (objectiveId: number, field: keyof PendingObjective, value: any) => void
  onUpdateKPIField: (objectiveId: number, kpiId: string, field: string, value: any) => void
  onAddKPI: (objectiveId: number) => void
  onRemoveKPI: (objectiveId: number, kpiId: string) => void
  onSetEditedObjectives: (fn: (prev: Record<number, PendingObjective>) => Record<number, PendingObjective>) => void
  onShowWithdrawDialog: (id: number) => void
  onShowDeleteDialog: (id: number) => void
}

export default function ObjectiveCard({
  objective,
  allowEdit = true,
  isEditing,
  currentObj,
  savingEdit,
  editedObjectives,
  canWithdrawObjectives,
  withdrawingObjective,
  userRole,
  objectivesLookup,
  initiativesLookup,
  operationalLookup,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onUpdateField,
  onUpdateKPIField,
  onAddKPI,
  onRemoveKPI,
  onSetEditedObjectives,
  onShowWithdrawDialog,
  onShowDeleteDialog
}: ObjectiveCardProps) {
  const [expandedKpiId, setExpandedKpiId] = useState<string | null>(null)
  // The year the objective is for, from its saved dates (an edit doesn't move it)
  const objYear = objectiveYear(objective)

  const formatMeasurement = (kpi: KPI) => {
    const mode = kpi.measurement_mode || 'numeric'
    if (mode === 'rating') return 'RAG'
    if (mode === 'binary') return 'Yes/No'
    return `${kpi.target_value} ${kpi.unit}`
  }

  const directionIcon = (kpi: KPI) => {
    if (kpi.measurement_mode === 'rating' || kpi.measurement_mode === 'binary') return null
    return kpi.measurement_direction === 'lower_is_better'
      ? <TrendingDown className="h-3 w-3 text-blue-500 inline ml-1" />
      : <TrendingUp className="h-3 w-3 text-green-500 inline ml-1" />
  }

  return (
    <CardContent className="space-y-4 border-t pt-4 overflow-hidden">
      {/* Information banner for rejected objectives */}
      {objective.status === 'rejected' && userRole === 'manager' && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex gap-2">
            <AlertCircle className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">Editing a rejected objective</p>
              <p>When you save changes to this objective, it will be automatically moved back to the <strong>Active</strong> tab and become an active objective again.</p>
            </div>
          </div>
        </div>
      )}

      {/* Edit Mode Toggle */}
      {allowEdit && (
        <div className="flex justify-between">
          <div className="flex gap-2">
            {/* Withdraw button - only for NEW objectives (never previously active), only before session scheduled */}
            {!isEditing && objective.status === 'pending_approval' && canWithdrawObjectives && !objective.was_previously_active && (
              (userRole === 'manager' && objective.role_type === 'manager-initiated') ||
              (userRole === 'employee' && objective.role_type === 'employee-initiated')
            ) && (
              <Button
                size="sm"
                variant="outline"
                className="text-orange-700 border-orange-300 hover:bg-orange-50"
                onClick={() => onShowWithdrawDialog(objective.id)}
                disabled={withdrawingObjective === objective.id}
              >
                <Undo2 className="h-4 w-4 mr-1" />
                Withdraw
              </Button>
            )}
            {/* Delete button for active objectives (manager only) - hide for rejected/pending */}
            {!isEditing && userRole === 'manager' && objective.status === 'active' && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onShowDeleteDialog(objective.id)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onCancelEdit(objective.id)}
                  disabled={savingEdit === objective.id}
                >
                  <XIcon className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => onSaveEdit(objective.id)}
                  disabled={savingEdit === objective.id}
                >
                  {savingEdit === objective.id ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      {objective.status === 'rejected' ? 'Reactivating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-1" />
                      {objective.status === 'rejected' ? 'Save & Reactivate' : 'Save Changes'}
                    </>
                  )}
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant={objective.status === 'rejected' ? 'default' : 'outline'}
                onClick={() => onStartEdit(objective)}
              >
                <Edit3 className="h-4 w-4 mr-1" />
                {objective.status === 'rejected' ? 'Revise & Reactivate' : 'Edit Objective'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Title & Description */}
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-semibold">Title</Label>
          {isEditing ? (
            <Input
              value={currentObj.title}
              onChange={(e) => onUpdateField(objective.id, 'title', e.target.value)}
              className="mt-1"
            />
          ) : (
            <p className="text-sm mt-1">{currentObj.title}</p>
          )}
        </div>

        <div>
          <Label className="text-sm font-semibold">Description</Label>
          {isEditing ? (
            <Textarea
              value={currentObj.description}
              onChange={(e) => onUpdateField(objective.id, 'description', e.target.value)}
              className="mt-1 min-h-[80px]"
            />
          ) : (
            <p className="text-sm text-muted-foreground mt-1 break-all">{currentObj.description}</p>
          )}
        </div>
      </div>

      {/* Rejection Reason - Display prominently for rejected objectives */}
      {(objective.status === 'rejected' || 'rejection_reason' in objective) && (objective as RejectedObjective).rejection_reason && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4">
          <Label className="text-sm font-semibold text-red-900 flex items-center gap-2">
            <XIcon className="h-4 w-4" />
            Rejection Reason
          </Label>
          <p className="text-sm text-red-700 mt-2">
            {(objective as RejectedObjective).rejection_reason}
          </p>
        </div>
      )}

      {/* Timeline: the objective's year, which an edit keeps it inside (HR UAT list #27) */}
      {objYear && (
        <p className="text-sm text-muted-foreground -mb-2">
          Objective for <span className="font-semibold text-foreground">{objYear}</span>
        </p>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-sm font-semibold">Start Date</Label>
          {isEditing ? (
            <Input
              type="date"
              min={objYear ? `${objYear}-01-01` : undefined}
              max={objYear ? `${objYear}-12-31` : undefined}
              value={currentObj.start_date?.split('T')[0] || ''}
              onChange={(e) => onUpdateField(objective.id, 'start_date', e.target.value)}
              className="mt-1"
            />
          ) : (
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {formatDate(currentObj.start_date)}
            </div>
          )}
        </div>
        <div>
          <Label className="text-sm font-semibold">End Date</Label>
          {isEditing ? (
            <Input
              type="date"
              min={objYear ? `${objYear}-01-01` : undefined}
              max={objYear ? `${objYear}-12-31` : undefined}
              value={currentObj.end_date?.split('T')[0] || ''}
              onChange={(e) => onUpdateField(objective.id, 'end_date', e.target.value)}
              className="mt-1"
            />
          ) : (
            <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              {formatDate(currentObj.end_date)}
            </div>
          )}
        </div>
      </div>

      {/* KPIs Section */}
      <Separator />
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Key Performance Indicators ({currentObj.kpis.length})
          </Label>
          {isEditing && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAddKPI(objective.id)}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add KPI
            </Button>
          )}
        </div>

        {currentObj.kpis.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg">
            <Goal className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm text-muted-foreground">No KPIs defined yet</p>
          </div>
        ) : isEditing ? (
          /* ── Edit Mode: Accordion KPIs ── */
          <div className="space-y-2">
            {currentObj.kpis.map((kpi, idx) => {
              const isExpanded = expandedKpiId === kpi.id
              return (
                <div key={kpi.id} className="border rounded-lg overflow-hidden">
                  {/* Collapsed summary row */}
                  <div
                    className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-slate-50 select-none"
                    onClick={() => setExpandedKpiId(isExpanded ? null : kpi.id)}
                  >
                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform shrink-0 ${isExpanded ? '' : '-rotate-90'}`} />
                    <span className="text-sm font-medium flex-1 truncate">{kpi.name || `KPI ${idx + 1}`}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatMeasurement(kpi)}</span>
                    <span className="text-xs text-muted-foreground shrink-0 capitalize">{kpi.frequency}</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-6 w-6 p-0 shrink-0"
                      onClick={(e) => { e.stopPropagation(); onRemoveKPI(objective.id, kpi.id) }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {/* Expanded edit form */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-2 border-t bg-slate-50/50 space-y-3">
                      <div>
                        <Label className="text-xs">KPI Name</Label>
                        <Input
                          value={kpi.name}
                          onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'name', e.target.value)}
                          placeholder="e.g., Student Pass Rate"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Description</Label>
                        <Textarea
                          value={kpi.description || ''}
                          onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'description', e.target.value)}
                          placeholder="Brief description"
                          className="mt-1 min-h-[60px]"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Measurement Type</Label>
                          <Select
                            value={kpi.measurement_mode || 'numeric'}
                            onValueChange={(value) => {
                              onUpdateKPIField(objective.id, kpi.id, 'measurement_mode', value)
                              if (value === 'percentage') onUpdateKPIField(objective.id, kpi.id, 'unit', '%')
                              else if (value === 'rating') { onUpdateKPIField(objective.id, kpi.id, 'target_value', 100); onUpdateKPIField(objective.id, kpi.id, 'unit', 'status') }
                              else if (value === 'binary') { onUpdateKPIField(objective.id, kpi.id, 'target_value', 100); onUpdateKPIField(objective.id, kpi.id, 'unit', 'achieved') }
                            }}
                          >
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="numeric">Numeric (count, days)</SelectItem>
                              <SelectItem value="percentage">Percentage (0-100%)</SelectItem>
                              <SelectItem value="rating">Rating Scale (RAG)</SelectItem>
                              <SelectItem value="binary">Binary (Achieved/Not)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Frequency</Label>
                          <Select value={kpi.frequency} onValueChange={(value) => onUpdateKPIField(objective.id, kpi.id, 'frequency', value)}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="bi-annually">Bi-annually</SelectItem>
                              <SelectItem value="annually">Annually</SelectItem>
                              <SelectItem value="quarterly">Quarterly</SelectItem>
                              <SelectItem value="semester">Semester</SelectItem>
                              <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      {(kpi.measurement_mode === 'rating' || kpi.measurement_mode === 'binary') ? (
                        <div className={`p-2 rounded text-xs ${kpi.measurement_mode === 'rating' ? 'bg-blue-50 text-blue-700' : 'bg-green-50 text-green-700'}`}>
                          {kpi.measurement_mode === 'rating' ? 'Rating: Red (0%) / Amber (50%) / Green (100%)' : 'Binary: Achieved (100%) or Not Achieved (0%)'}
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label className="text-xs">Target</Label>
                            <Input
                              type="number"
                              min={(kpi.measurement_mode || 'numeric') === 'percentage' ? 0 : undefined}
                              max={(kpi.measurement_mode || 'numeric') === 'percentage' ? 100 : undefined}
                              value={kpi.target_value}
                              onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'target_value', parseFloat(e.target.value))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Unit</Label>
                            {(kpi.measurement_mode || 'numeric') === 'percentage' ? (
                              <Input value="%" disabled className="mt-1 bg-gray-100" />
                            ) : (
                              <Input value={kpi.unit} onChange={(e) => onUpdateKPIField(objective.id, kpi.id, 'unit', e.target.value)} placeholder="days, count" className="mt-1" />
                            )}
                          </div>
                        </div>
                      )}
                      {(kpi.measurement_mode || 'numeric') !== 'rating' && (kpi.measurement_mode || 'numeric') !== 'binary' && (
                        <div>
                          <Label className="text-xs">Progress Direction</Label>
                          <Select value={kpi.measurement_direction || 'higher_is_better'} onValueChange={(value) => onUpdateKPIField(objective.id, kpi.id, 'measurement_direction', value)}>
                            <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="higher_is_better">↑ Higher is Better</SelectItem>
                              <SelectItem value="lower_is_better">↓ Lower is Better</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          /* ── View Mode: Compact Table ── */
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-3 py-2 font-medium text-xs text-slate-500">KPI</th>
                  <th className="px-3 py-2 font-medium text-xs text-slate-500 text-right w-20">Target</th>
                  <th className="px-3 py-2 font-medium text-xs text-slate-500 w-16">Unit</th>
                  <th className="px-3 py-2 font-medium text-xs text-slate-500 w-24">Frequency</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {currentObj.kpis.map((kpi) => (
                  <tr key={kpi.id} className="hover:bg-slate-50/50">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <span className="font-medium truncate">{kpi.name}</span>
                        {directionIcon(kpi)}
                        {kpi.description && (
                          <span title={kpi.description} className="shrink-0 cursor-help"><Info className="h-3.5 w-3.5 text-slate-400" /></span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                      {kpi.measurement_mode === 'rating' ? 'RAG' : kpi.measurement_mode === 'binary' ? '—' : kpi.target_value}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">{kpi.unit}</td>
                    <td className="px-3 py-2 text-muted-foreground capitalize">{kpi.frequency}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* SMART Analysis — real AI assessment, persisted with the objective */}
      {!isEditing && currentObj.smart_analysis && (
        <>
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-heritage-blue" />
                SMART Analysis
              </Label>
              {typeof currentObj.smart_analysis.overallScore === 'number' && (
                <Badge className={
                  currentObj.smart_analysis.overallScore >= 80 ? 'bg-green-100 text-green-800'
                  : currentObj.smart_analysis.overallScore >= 60 ? 'bg-amber-100 text-amber-800'
                  : 'bg-red-100 text-red-800'
                }>
                  {currentObj.smart_analysis.overallScore}/100
                </Badge>
              )}
            </div>
            <div className="grid gap-2">
              {([
                ['specific', 'Specific'],
                ['measurable', 'Measurable'],
                ['achievable', 'Achievable'],
                ['relevant', 'Relevant'],
                ['timeBound', 'Time-bound'],
              ] as const).map(([key, label]) => {
                const c = (currentObj.smart_analysis as any)?.[key]
                if (!c) return null
                return (
                  <div key={key} className="border rounded-lg p-2.5 bg-slate-50/50">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{label}</span>
                      {typeof c.score === 'number' && (
                        <span className={`text-xs font-bold ${c.score >= 80 ? 'text-green-600' : c.score >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                          {c.score}/100
                        </span>
                      )}
                    </div>
                    {c.feedback && <p className="text-xs text-muted-foreground mt-1">{c.feedback}</p>}
                  </div>
                )
              })}
            </div>
          </div>
        </>
      )}

      {/* Strategic Context (L1-L3) */}
      <Separator />
      {isEditing ? (
        <StrategicContextSelector
          value={{
            strategic_objective_id: currentObj.strategic_objective_id,
            strategic_initiative_id: currentObj.strategic_initiative_id,
            operational_initiative_id: currentObj.operational_initiative_id
          }}
          onChange={(context: StrategicContext) => {
            onSetEditedObjectives(prev => ({
              ...prev,
              [objective.id]: {
                ...prev[objective.id],
                strategic_objective_id: context.strategic_objective_id,
                strategic_initiative_id: context.strategic_initiative_id,
                operational_initiative_id: context.operational_initiative_id
              }
            }))
          }}
          compact
        />
      ) : (
        <div>
          <Label className="text-sm font-semibold flex items-center gap-2">
            <Goal className="h-4 w-4" />
            Strategic Context (Optional)
          </Label>
          {(currentObj.strategic_objective_id || currentObj.strategic_initiative_id || currentObj.operational_initiative_id) ? (
            <div className="flex flex-wrap gap-2 mt-2">
              {currentObj.strategic_objective_id && objectivesLookup.find(o => o.id === currentObj.strategic_objective_id) && (() => {
                const strategicObj = objectivesLookup.find(o => o.id === currentObj.strategic_objective_id)!
                return (
                  <span title={`L1 Strategic Objective: ${strategicObj.display_name}`}>
                    <Badge
                      style={{ backgroundColor: strategicObj.color_code || '#6B7280' }}
                      className="text-white"
                    >
                      L1: {strategicObj.code}. {strategicObj.display_name}
                    </Badge>
                  </span>
                )
              })()}
              {currentObj.strategic_initiative_id && initiativesLookup.find(i => i.id === currentObj.strategic_initiative_id) && (() => {
                const initiative = initiativesLookup.find(i => i.id === currentObj.strategic_initiative_id)!
                return (
                  <span title={`L2 Strategic Initiative: ${initiative.name}`}>
                    <Badge variant="outline">
                      L2: {initiative.code} {initiative.name}
                    </Badge>
                  </span>
                )
              })()}
              {currentObj.operational_initiative_id && operationalLookup.find(o => o.id === currentObj.operational_initiative_id) && (() => {
                const operational = operationalLookup.find(o => o.id === currentObj.operational_initiative_id)!
                return (
                  <span title={`L3 Operational Initiative: ${operational.name}`}>
                    <Badge variant="outline" className="text-xs">
                      L3: {operational.code}
                    </Badge>
                  </span>
                )
              })()}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground mt-2">No strategic context linked</p>
          )}
        </div>
      )}

      {/* Legacy Strategic Alignment - show if old data exists */}
      {currentObj.strategic_objective && !currentObj.strategic_objective_id && (
        <div className="mt-3">
          <Label className="text-sm font-semibold">Aligned with Strategic Objective</Label>
          <div className="mt-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-900 flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              {currentObj.strategic_objective.title}
            </p>
          </div>
        </div>
      )}
    </CardContent>
  )
}
